<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  counts: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
});

const { t } = useI18n();

const categories = computed(() => [
  {
    id: 'rascunhos',
    label: t('SALES.LISTS.DRAFTS'),
    count: Number(props.counts?.rascunhos || 0),
    strokeClass: 'stroke-[#13D0AB]',
  },
  {
    id: 'entregues',
    label: t('SALES.LISTS.DELIVERED'),
    count: Number(props.counts?.entregues || 0),
    strokeClass: 'stroke-[#FD7622]',
  },
  {
    id: 'revisoes',
    label: t('SALES.LISTS.REVIEW'),
    count: Number(props.counts?.revisoes || 0),
    strokeClass: 'stroke-[#FFC43E]',
  },
]);

const total = computed(() =>
  categories.value.reduce((sum, category) => sum + category.count, 0)
);

const distribution = computed(() => {
  let offset = 0;

  return categories.value.map(category => {
    const ratio = total.value ? (category.count / total.value) * 100 : 0;
    const segment = { ...category, offset: -offset, ratio };
    offset += ratio;
    return segment;
  });
});
</script>

<template>
  <section
    class="overflow-hidden rounded-lais border border-lais-border-soft bg-white shadow-lais-card"
  >
    <div
      class="flex min-h-12 items-center gap-2 border-b border-lais-border-soft px-4 py-3 sm:px-5"
    >
      <h2 class="font-redHatDisplay text-lg font-semibold text-lais-text">
        {{ t('SALES.OVERVIEW.TOTAL') }}
      </h2>
      <span
        class="rounded-full bg-lais-primary-50 px-2 py-0.5 text-xs font-bold text-lais-primary"
      >
        {{ total }}
      </span>
    </div>

    <div
      v-if="loading"
      class="m-5 h-28 animate-pulse rounded-lais bg-lais-bg-soft"
    />
    <div v-else class="grid gap-5 p-5">
      <svg
        class="mx-auto size-40"
        data-testid="sales-overview-donut"
        viewBox="0 0 100 100"
        role="img"
        :aria-label="t('SALES.OVERVIEW.TOTAL')"
      >
        <circle
          class="stroke-lais-bg-soft"
          cx="50"
          cy="50"
          fill="none"
          pathLength="100"
          r="38"
          stroke-width="16"
        />
        <circle
          v-for="category in distribution"
          :key="category.id"
          :class="category.strokeClass"
          :stroke-dasharray="`${category.ratio} ${100 - category.ratio}`"
          :stroke-dashoffset="category.offset"
          cx="50"
          cy="50"
          fill="none"
          pathLength="100"
          r="38"
          stroke-linecap="butt"
          stroke-width="16"
          transform="rotate(-90 50 50)"
        />
        <text
          class="fill-lais-text text-[14px] font-bold"
          x="50"
          y="53"
          text-anchor="middle"
        >
          {{ total }}
        </text>
      </svg>

      <div class="grid grid-cols-3 gap-3 text-center">
        <div
          v-for="category in categories"
          :key="category.id"
          class="grid gap-1"
        >
          <span class="text-lg font-bold text-lais-text">
            {{ category.count }}
          </span>
          <span class="text-xs text-lais-text-muted">{{ category.label }}</span>
        </div>
      </div>
    </div>
  </section>
</template>
